import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import Select from "react-select";
import Spinwheel from "../../components/common/Spinwheel";
import { useUserContext } from "../../context/userContext";
import { getCurrentSiteSettingsApi, getProfileApi, statesAndCitiesApi, updateCurrentSiteSettingsApi } from "../../utils/ApiEndpointFunctions";
import "./SiteSettings.css";

const emptyForm = {
  name: "",
  description: "",
  agreementNo: "",
  clientDepartment: "",
  consultantName: "",
  contactName: "",
  contactPhone: "",
  email: "",
  address: { address1: "", address2: "", city: "", state: "", pinCode: "", stateCode: "" },
};

const toForm = (data = {}) => ({
  name: data.name || "",
  description: data.description || "",
  agreementNo: data.agreementNo || "",
  clientDepartment: data.clientDepartment || "",
  consultantName: data.consultantName || "",
  contactName: data.contactName || "",
  contactPhone: data.contactPhone || "",
  email: data.email || "",
  address: { ...emptyForm.address, ...(data.address || {}) },
});

export default function SiteSettings() {
  const { langPreference, setUserDetails } = useUserContext();
  const [form, setForm] = useState(emptyForm);
  const [savedForm, setSavedForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const loadCities = useCallback(async (stateId) => {
    if (!stateId) {
      setCityOptions([]);
      return;
    }
    setLoadingCities(true);
    try {
      const response = await statesAndCitiesApi(stateId);
      setCityOptions((response?.data?.data?.cities || []).map((city) => ({ label: city.city, value: city })));
    } catch (error) {
      setCityOptions([]);
      toast.error(error?.response?.data?.message || "Unable to load cities.");
    } finally {
      setLoadingCities(false);
    }
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [response, locationResponse] = await Promise.all([
        getCurrentSiteSettingsApi(),
        statesAndCitiesApi().catch(() => null),
      ]);
      const nextForm = toForm(response?.data?.data);
      const nextStateOptions = (locationResponse?.data?.data?.state || []).map((state) => ({ label: state.state, value: state }));
      setForm(nextForm);
      setSavedForm(nextForm);
      setStateOptions(nextStateOptions);
      const selectedState = nextStateOptions.find((option) => option.label.toLowerCase() === nextForm.address.state.toLowerCase());
      if (selectedState) await loadCities(selectedState.value._id);
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to load site settings.";
      setLoadError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [loadCities]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const updateAddress = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, address: { ...current.address, [name]: value } }));
  };

  const selectedState = stateOptions.find((option) => option.label.toLowerCase() === form.address.state.toLowerCase())
    || (form.address.state ? { label: form.address.state, value: null } : null);
  const selectedCity = cityOptions.find((option) => option.label.toLowerCase() === form.address.city.toLowerCase())
    || (form.address.city ? { label: form.address.city, value: null } : null);

  const resetForm = () => {
    setForm(savedForm);
    const savedState = stateOptions.find((option) => option.label.toLowerCase() === savedForm.address.state.toLowerCase());
    if (savedState) loadCities(savedState.value._id);
    else setCityOptions([]);
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error(langPreference?.site_name_required || "Site name is required.");
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      return toast.error(langPreference?.invalid_email || "Enter a valid email address.");
    }

    setSaving(true);
    try {
      const response = await updateCurrentSiteSettingsApi(form);
      const nextForm = toForm(response?.data?.data);
      setForm(nextForm);
      setSavedForm(nextForm);
      const profile = await getProfileApi();
      if (profile?.data?.data) setUserDetails(profile.data.data);
      toast.success(response?.data?.message || "Site settings updated successfully.");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update site settings.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="main-content"><div className="site-settings__loading"><Spinwheel /></div></div>;
  if (loadError) return <div className="main-content"><div className="container-fluid py-4"><div className="alert alert-danger">
    <h5>{langPreference?.site_settings_load_failed || "Unable to load site settings"}</h5>
    <p>{loadError}</p>
    <button className="btn btn-outline-danger" onClick={loadSettings}>{langPreference?.retry || "Retry"}</button>
  </div></div></div>;

  return <div className="main-content site-settings"><div className="container-fluid">
    <div className="site-settings__heading">
      <div>
        <h3 className="font-blue mb-1">{langPreference?.site_settings || "Site Settings"}</h3>
        <p className="text-muted mb-0">{langPreference?.site_settings_description || "Update details for the currently selected site."}</p>
      </div>
      <div className="site-settings__current"><i className="bi bi-geo-alt-fill" aria-hidden="true"></i><span>{form.name}</span></div>
    </div>

    <form onSubmit={saveSettings}>
      <section className="site-settings__section">
        <div className="site-settings__section-title"><i className="bi bi-building"></i><div><h4>{langPreference?.site_details || "Site Details"}</h4><p>{langPreference?.site_details_help || "The site name appears in the header and on site documents."}</p></div></div>
        <div className="row g-3">
          <div className="col-lg-6"><label className="form-label">{langPreference?.site_name || "Site Name"} *</label><input className="form-control" name="name" value={form.name} maxLength="120" onChange={updateField} required /></div>
          <div className="col-lg-6"><label className="form-label">{langPreference?.site_agreement_no || "Agreement No"}</label><input className="form-control" name="agreementNo" value={form.agreementNo} maxLength="120" onChange={updateField} /></div>
          <div className="col-lg-6"><label className="form-label">{langPreference?.client_department || "Client / Department Name"}</label><input className="form-control" name="clientDepartment" value={form.clientDepartment} maxLength="200" onChange={updateField} /></div>
          <div className="col-lg-6"><label className="form-label" htmlFor="site-consultant-name">{langPreference?.consultant_name || "Consultant Name / PMC"}</label><input id="site-consultant-name" className="form-control" name="consultantName" value={form.consultantName} maxLength="200" onChange={updateField} /></div>
          <div className="col-12"><label className="form-label">{langPreference?.description || "Description"}</label><input className="form-control" name="description" value={form.description} maxLength="1000" onChange={updateField} /></div>
        </div>
      </section>

      <section className="site-settings__section">
        <div className="site-settings__section-title"><i className="bi bi-person-lines-fill"></i><div><h4>{langPreference?.contact_details || "Contact Details"}</h4><p>{langPreference?.contact_details_help || "Keep the primary site contact information up to date."}</p></div></div>
        <div className="row g-3">
          <div className="col-lg-4"><label className="form-label">{langPreference?.contact_name || "Contact Name"}</label><input className="form-control" name="contactName" value={form.contactName} maxLength="120" onChange={updateField} /></div>
          <div className="col-lg-4"><label className="form-label">{langPreference?.contact_phone || "Contact Phone"}</label><input className="form-control" name="contactPhone" value={form.contactPhone} maxLength="30" onChange={updateField} /></div>
          <div className="col-lg-4"><label className="form-label">{langPreference?.email || "Email"}</label><input type="email" className="form-control" name="email" value={form.email} maxLength="254" onChange={updateField} /></div>
        </div>
      </section>

      <section className="site-settings__section">
        <div className="site-settings__section-title"><i className="bi bi-map"></i><div><h4>{langPreference?.address || "Address"}</h4><p>{langPreference?.address_help || "This address is used wherever the selected site's details are shown."}</p></div></div>
        <div className="row g-3">
          <div className="col-lg-6"><label className="form-label">{langPreference?.address_line_1 || "Address Line 1"}</label><input className="form-control" name="address1" value={form.address.address1} maxLength="250" onChange={updateAddress} /></div>
          <div className="col-lg-6"><label className="form-label">{langPreference?.address_line_2 || "Address Line 2"}</label><input className="form-control" name="address2" value={form.address.address2} maxLength="250" onChange={updateAddress} /></div>
          <div className="col-md-4"><label className="form-label">{langPreference?.state || "State"}</label><Select classNamePrefix="select" value={selectedState} options={stateOptions} isClearable isSearchable menuPlacement="top" maxMenuHeight={320} menuShouldScrollIntoView={false} placeholder={langPreference?.select_state || "Select state"} onChange={(option) => {
            setForm((current) => ({ ...current, address: { ...current.address, state: option?.label || "", city: "" } }));
            if (option?.value?._id) loadCities(option.value._id);
            else setCityOptions([]);
          }} /></div>
          <div className="col-md-4"><label className="form-label">{langPreference?.city || "City"}</label><Select classNamePrefix="select" value={selectedCity} options={cityOptions} isClearable isSearchable isLoading={loadingCities} isDisabled={!form.address.state || loadingCities} menuPlacement="top" maxMenuHeight={320} menuShouldScrollIntoView={false} placeholder={langPreference?.select_city || "Select city"} onChange={(option) => setForm((current) => ({ ...current, address: { ...current.address, city: option?.label || "" } }))} /></div>
          <div className="col-md-4"><label className="form-label">{langPreference?.pin || "PIN"}</label><input className="form-control" name="pinCode" value={form.address.pinCode} maxLength="20" onChange={updateAddress} /></div>
        </div>
      </section>

      <div className="site-settings__actions">
        <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={resetForm}>{langPreference?.reset || "Reset"}</button>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? (langPreference?.saving || "Saving...") : (langPreference?.save_changes || "Save Changes")}</button>
      </div>
    </form>
  </div></div>;
}
